import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import type { AppDispatch, RootState } from '../store';
import { RequestStatus } from '../utils';
import type { ConsentTemplate } from './admin';

/**
 * Consumer-safe projection of an approved Lead-Share enrolment (PTEC-518).
 *
 * Deliberately a SUBSET of the admin `LeadShareEnrollment`: it omits every
 * commercial / operational field (`pricePerLead`, `currency`, `deliveryMode`,
 * `endpointRef`, `credentialsRef`, and the raw `audienceRules`). The end user
 * must never receive those — data-minimisation, per the Lead-Share DPIA. The
 * server returns only opportunities the caller is eligible for and that are
 * active with approved, versioned copy; the client never decides eligibility.
 */
export interface LeadShareOpportunity {
	partnerOrganisationId: string;
	partnerName: string;
	/** Structured consent slots (headline/blurb/CTAs/purpose/privacy URL). */
	consentTemplate: ConsentTemplate;
	/** Version of the approved copy the user is being shown — recorded with consent. */
	consentCopyVersion: number;
	/**
	 * Fields that would transmit on acceptance — used ONLY to render the
	 * disclosure sentence (labels), never the user's actual values.
	 */
	fieldSet: string[];
	placement: 'dashboard' | 'firstLogin';
	/**
	 * Self-select interests this opportunity is offered under (PTEC-568/569). When
	 * present, the surface shows only if the user has opted into a matching
	 * interest; empty/absent = not self-select-gated (e.g. targeted-only). The
	 * server sends only the user's own matching keys, never the raw audience rules.
	 */
	matchInterestKeys?: string[];
}

interface LeadShareOpportunitiesState {
	items: LeadShareOpportunity[];
	status: RequestStatus;
}

const initialState: LeadShareOpportunitiesState = {
	items: [],
	status: RequestStatus.IDLE
};

/**
 * Consumer-scoped read (PTEC-518). Safe projection only. Returns `[]` when the
 * user is not eligible or no partner has approved, active copy. The route is
 * user-authenticated (not `/admins/...`) and must strip commercial fields
 * server-side before responding.
 */
export const fetchLeadShareOpportunities = createAsyncThunk<
	LeadShareOpportunity[],
	void,
	{ dispatch: AppDispatch; state: RootState }
>('leadShareOpportunities/fetch', async () => {
	const response = await API.get('inspirusapi', '/lead-share/opportunities', {});
	return Array.isArray(response?.items) ? response.items : [];
});

/**
 * Consent recording (PTEC-565). Persists the accept/decline as a write-once,
 * versioned consent record (the Art. 7(1) evidence — see the Consent Design
 * page) and durably suppresses the decided opportunity: the consumer read
 * excludes it server-side on every later fetch, across sessions and devices.
 * No lead data is transmitted — only the decision and the versions it pins.
 */
export const recordConsentDecision = createAsyncThunk<
	void,
	{
		partnerOrganisationId: string;
		decision: 'accepted' | 'declined';
		consentCopyVersion: number;
		placement: LeadShareOpportunity['placement'];
	},
	{ dispatch: AppDispatch; state: RootState }
>('leadShareOpportunities/recordConsentDecision', async (payload) => {
	await API.post('inspirusapi', '/lead-share/consent', { body: payload });
});

/**
 * Funnel touch telemetry (PTEC-535): client-observed pre-consent stages only
 * (impression | click) — later stages are recorded server-side where they
 * happen. Fire-and-forget: telemetry must never affect the widget UX.
 */
export const recordFunnelTouch = createAsyncThunk<
	void,
	{
		type: 'impression' | 'click';
		partnerOrganisationId: string;
		placement: LeadShareOpportunity['placement'];
		copyVersion: number;
	},
	{ dispatch: AppDispatch; state: RootState }
>('leadShareOpportunities/recordFunnelTouch', async (payload) => {
	await API.post('inspirusapi', '/lead-share/events', {
		body: {
			type: payload.type,
			partnerOrganisationId: payload.partnerOrganisationId,
			placement: payload.placement,
			copyVersion: payload.copyVersion
		}
	});
});

const leadShareOpportunitiesSlice = createSlice({
	name: 'leadShareOpportunities',
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addCase(fetchLeadShareOpportunities.pending, (state) => {
			state.status = RequestStatus.PENDING;
		});
		builder.addCase(fetchLeadShareOpportunities.fulfilled, (state, action) => {
			state.items = action.payload;
			state.status = RequestStatus.SUCCESS;
		});
		builder.addCase(fetchLeadShareOpportunities.rejected, (state) => {
			state.items = [];
			state.status = RequestStatus.ERROR;
		});
	}
});

export default leadShareOpportunitiesSlice.reducer;
